import {
  addLineItems,
  checkout,
  getCart,
  getCartError,
} from '@/cart/cart-store'
import { Cart } from '@/cart/types'
import { effect } from '@preact/signals'
import CtaButtonGroup23 from './CtaButtonGroup23'

/**
 * BS_23 "Tiered Single Variant" Buy-stack component
 *
 * Single-variant tiered buy stack with OTP and Subscription support.
 * Quantities are selected via radio tiers; selling plans are mapped
 * server-side into a JSON lookup keyed by quantity.
 */
export default class BuyStack23 extends HTMLElement {
  private _variantId = 0
  private _tierQuantity = 1
  private _isSubscription = false
  private _cart: Cart | null = null
  private _cartError: string | null = null
  private _pendingCheckout = false
  private _acceleratedCheckoutInFlight = false

  /** Server-built map: quantity (number) → selling plan id (number) */
  private sellingPlanMap: Record<number, number> = {}
  private otpQuantities: number[] = []
  private subQuantities: number[] = []

  constructor() {
    super()
    this._variantId = Number(this.getAttribute('variant-id')) || 0
    this._tierQuantity = Number(this.getAttribute('initial-quantity')) || 1
    this._isSubscription = this.getAttribute('initial-mode') === 'subscription'
    this.sellingPlanMap = JSON.parse(this.getAttribute('selling-plan-map') ?? '{}') as Record<number, number>
    this.otpQuantities = (JSON.parse(this.getAttribute('otp-quantities') ?? '[]') as number[]).map(Number)
    this.subQuantities = (JSON.parse(this.getAttribute('sub-quantities') ?? '[]') as number[]).map(Number)
  }

  connectedCallback() {
    this._cart = getCart()
    this.setupListeners()
    // Defer signal subscriptions to next microtask so the component is fully mounted
    setTimeout(() => {
      effect(() => { this.cart = getCart() })
      effect(() => { this.cartError = getCartError() })
    })
    this.render()
  }

  // --- Reactive setters ---

  private get pendingCheckout(): boolean {
    return this._pendingCheckout
  }

  private set pendingCheckout(value: boolean) {
    this._pendingCheckout = value
    this.render()
  }

  private get cart() {
    return this._cart
  }

  private set cart(value: Cart | null) {
    this._cart = value
    if (value) this.render()
  }

  private get cartError() {
    return this._cartError
  }

  private set cartError(value: string | null) {
    this._cartError = value
    if (value) this.render()
  }

  public get tierQuantity(): number {
    return this._tierQuantity
  }

  public get isSubscription(): boolean {
    return this._isSubscription
  }

  // --- Listeners ---

  private setupListeners() {
    this.onchange = (e: Event) => this.handleChange(e)
    this.addEventListener('checkout', () => this.handleAddToCartAndCheckout())
    this.setupProgressNodeClicks()
    this.setupAcceleratedCheckoutInterception()
  }

  private handleChange(e: Event): void {
    const target = e.target
    if (!(target instanceof HTMLInputElement)) return

    if (target.name === 'otp-tier-quantity' || target.name === 'sub-tier-quantity') {
      this._tierQuantity = Number(target.value)
      this.render()
    }

    if (target.name === 'is-subscription') {
      this._isSubscription = target.checked
      // Snap to last tier of the new mode
      const targetArr = this._isSubscription ? this.subQuantities : this.otpQuantities
      if (targetArr.length > 0) {
        this._tierQuantity = targetArr[targetArr.length - 1]
        // Sync radio state
        this.syncRadioSelection()
      }
      this.render()
    }
  }

  private setupProgressNodeClicks() {
    this.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as Element
      const node = target.closest<HTMLButtonElement>('[data-tier-node]')
      if (!node) return
      const qty = Number(node.dataset.tierNode)
      if (!qty) return
      this._tierQuantity = qty
      this.syncRadioSelection()
      this.render()
    })
  }

  private syncRadioSelection() {
    const name = this._isSubscription ? 'sub-tier-quantity' : 'otp-tier-quantity'
    const radio = this.querySelector<HTMLInputElement>(
      `input[name="${name}"][value="${this._tierQuantity}"]`
    )
    if (radio) radio.checked = true
  }

  private setupAcceleratedCheckoutInterception() {
    this.addEventListener('click', async (e: MouseEvent) => {
      if (this._acceleratedCheckoutInFlight) return
      const target = e.target as Element
      if (target.closest('[data-action="checkout"]')) return
      if (!target.closest('cta-button-group-23')) return
      const btn = target.closest<HTMLElement>('button, [role="button"]')
      if (!btn) return

      e.stopPropagation()
      e.preventDefault()

      if (!this._variantId || this._tierQuantity < 1) return

      this._acceleratedCheckoutInFlight = true
      try {
        await addLineItems([{
          id: this._variantId,
          quantity: this._tierQuantity,
          selling_plan: this.getSellingPlanId() ?? undefined,
        }])
        btn.click()
      } finally {
        this._acceleratedCheckoutInFlight = false
      }
    }, true)
  }

  // --- Cart ---

  private getSellingPlanId(): number | null {
    if (!this._isSubscription) return null
    const planId = this.sellingPlanMap[this._tierQuantity]
    return planId != null ? Number(planId) : null
  }

  public async handleAddToCartAndCheckout() {
    if (this._pendingCheckout) return
    if (!this._variantId || this._tierQuantity < 1) return

    this.pendingCheckout = true
    // Disable toggle during in-flight request
    this.toggleSubscriptionControl(true)

    await addLineItems([{
      id: this._variantId,
      quantity: this._tierQuantity,
      selling_plan: this.getSellingPlanId() ?? undefined,
    }])
    await checkout()

    // If checkout doesn't redirect (error), reset state after timeout
    setTimeout(() => {
      this.pendingCheckout = false
      this.toggleSubscriptionControl(false)
    }, 2000)
  }

  private toggleSubscriptionControl(disabled: boolean) {
    const toggle = this.querySelector<HTMLInputElement>('input[name="is-subscription"]')
    if (toggle) toggle.disabled = disabled
  }

  // --- Render ---

  private render() {
    // Update tier selected state via CSS classes
    this.updateTierVisuals()

    // Update progress indicator
    this.updateProgressIndicator()

    // Update OTP/Sub visibility
    this.updateModeVisibility()

    // Update CTA button state
    const btnGroup = this.querySelector<CtaButtonGroup23>('cta-button-group-23')
    if (btnGroup) {
      btnGroup.loading = this.pendingCheckout
    }

    // Cart error message
    this.renderCartError()
  }

  private updateTierVisuals() {
    // Update checked styling via radio state
    const radios = this.querySelectorAll<HTMLInputElement>('input[name="otp-tier-quantity"], input[name="sub-tier-quantity"]')
    radios.forEach(radio => {
      const label = radio.closest('label')
      if (!label) return
      if (Number(radio.value) === this._tierQuantity && this.isRadioInActiveMode(radio)) {
        radio.checked = true
      }
    })
  }

  private isRadioInActiveMode(radio: HTMLInputElement): boolean {
    if (this._isSubscription) return radio.name === 'sub-tier-quantity'
    return radio.name === 'otp-tier-quantity'
  }

  private updateProgressIndicator() {
    // Update progress bar width via data attributes
    const activeIndicator = this._isSubscription
      ? this.querySelector('[data-progress="sub"]')
      : this.querySelector('[data-progress="otp"]')

    if (!activeIndicator) return

    const bar = activeIndicator.querySelector<HTMLElement>('[data-progress-bar]')
    const widthMap = JSON.parse(activeIndicator.getAttribute('data-width-map') ?? '{}') as Record<number, number>
    if (bar) {
      bar.style.width = `${widthMap[this._tierQuantity] ?? 0}%`
    }

    // Update node active states
    activeIndicator.querySelectorAll<HTMLElement>('[data-tier-node]').forEach(node => {
      const nodeQty = Number(node.dataset.tierNode)
      const isActive = this._tierQuantity >= nodeQty
      node.classList.toggle('border-deep-ocean-blue', isActive)
      node.classList.toggle('border-deep-ocean-blue/20', !isActive)
      const inner = node.querySelector<HTMLElement>('[data-node-fill]')
      if (inner) {
        inner.classList.toggle('bg-deep-ocean-blue', isActive)
        inner.classList.toggle('bg-transparent', !isActive)
      }
    })

    // Update badge active states
    activeIndicator.querySelectorAll<HTMLElement>('[data-tier-badge]').forEach(badge => {
      const badgeQty = Number(badge.dataset.tierBadge)
      const isActive = this._tierQuantity >= badgeQty
      badge.classList.toggle('bg-[#e9e6f2]', isActive)
      badge.classList.toggle('bg-neutral-gray/50', !isActive)
      badge.classList.toggle('text-dark-blue-gray', !isActive)
      badge.classList.toggle('opacity-50', !isActive)
    })
  }

  private updateModeVisibility() {
    // Show/hide OTP vs Sub sections
    this.querySelectorAll<HTMLElement>('[data-mode]').forEach(el => {
      const mode = el.dataset.mode
      if (mode === 'otp') {
        el.hidden = this._isSubscription
      } else if (mode === 'sub') {
        el.hidden = !this._isSubscription
      }
    })
  }

  private renderCartError() {
    const errorContainer = this.querySelector('#cart-error')
    const errorSpan = errorContainer?.querySelector('span')
    if (!errorContainer || !errorSpan) return

    errorContainer.classList.toggle('opacity-0', !this.cartError)
    errorContainer.classList.toggle('max-h-0', !this.cartError)
    errorContainer.classList.toggle('max-h-12', !!this.cartError)
    errorContainer.classList.toggle('pointer-events-none', !this.cartError)

    if (this.cartError) {
      errorSpan.textContent = this.cartError
      errorSpan.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      errorContainer.addEventListener(
        'transitionend',
        () => { errorSpan.textContent = '' },
        { once: true }
      )
    }
  }
}
